import { AuthenticationService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import SmartcareTranscription, { RecordingState } from './smartcare-transcription';
import ShowToast from '../../../../utils/toast-service';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { SmartcareActions } from './smartcare-actions';

type Segments = {
  endTime: number,
  speakerLabel: string,
  startTime: number,
  transcript: string,
}

type Response = {
  id: string
  info: {
    executionTime: number,
    model: string,
    transcriptionTime: number
  }
  output: {
    duration: number,
    segments: Segments[],
    transcription: string
  }
}

export class SmartCareAudioTranscription extends SmartcareTranscription implements SmartcareActions {

  private smartCareUrl: string;

  constructor(smartCareUrl: string) {
    super()
    this.smartCareUrl = smartCareUrl;
  }

  public async start(): Promise<void> {
    if (this.audiRecordService?.state === RecordingState.RECORDING) {
      this.stop();
    }

    try {
      await this.audiRecordService.start();
    } catch (error) {
      ShowToast.open(MessageService.translate('text.microphone-not-recognized'), ToastLevel.ERROR);
      return Promise.reject(new Error(MessageService.translate('text.microphone-not-recognized')))
    }
    this.timerService.startTimer()
    return Promise.resolve();
  }

  public async stop(): Promise<void> {
    return
  }

  public async pause(): Promise<void> {
    if (this.audiRecordService?.state !== RecordingState.RECORDING) {
      return Promise.reject(new Error('Recording is not active'));
    }
    this.timerService.stopTimer()
    const audio: Blob = await this.audiRecordService.stop();
    return this.executeAudioInput(audio)
      .then((response) => {
        response.output.segments.forEach((segment) => {
          super.addTranscription({
            speaker: segment.speakerLabel.split('_')[1],
            timer: String(segment.startTime),
            transcript: segment.transcript
          })
        })
      })
  }

  public async resume(): Promise<void> {
    if (this.audiRecordService) {
      this.audiRecordService.resume();
      this.timerService.startTimer()
    }
  }

  private async executeAudioInput(audio: Blob): Promise<Response> {
    const transcriptionAudioApi = '/api/services/transcription';
    const token = AuthenticationService.getJWTToken()?.rawToken();
    const url = this.smartCareUrl.endsWith('/') ? this.smartCareUrl.slice(0, -1) : this.smartCareUrl
    return fetch(`${url}${transcriptionAudioApi}`, {
      body: JSON.stringify({
        input: await this.parseFile(audio)
      }),
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'input-file-format': 'm4a'
      },
      method: 'POST',
    }).then((response) => response.json())
      .then((data: Response) => data)
  }

  private async parseFile(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function (event) {
        if (typeof event.target?.result !== 'string') {
          reject(new Error('Could not read file'));
          return;
        }
        const base64String = event.target.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = () => {
        reject(new Error('Error parsing file'));
      };
    })
  }
}

